import { useEffect, useState } from "react"
import { Link } from "react-router-dom"
import { Button } from "@/components/ui/button"
import { Badge } from "@/components/ui/badge"
import { fetchNotifications, markNotificationRead, markAllNotificationsRead } from "~/apis/notifications"
import { fetchGuestReminders, dismissGuestReminder, snoozeGuestReminder } from "~/apis/guestReminders"
import { fetchCleanerNotes, resolveCleanerNote } from "~/apis/cleanerNotes"
import routes from "~/routes"

const REMINDER_LABELS = {
  checkin_instructions: "Send check-in info via Riparide",
  welcome: "Send welcome message",
  checkout: "Send check-out note (this morning)",
}

function timeAgo(iso) {
  const seconds = Math.max(0, Math.floor((Date.now() - new Date(iso).getTime()) / 1000))
  const units = [ [ "year", 31536000 ], [ "month", 2592000 ], [ "day", 86400 ], [ "hour", 3600 ], [ "minute", 60 ] ]

  for (const [ unit, secondsPerUnit ] of units) {
    const value = Math.floor(seconds / secondsPerUnit)
    if (value >= 1) return `${value} ${unit}${value === 1 ? "" : "s"} ago`
  }

  return "less than a minute ago"
}

function formatDate(iso) {
  return new Date(`${iso}T00:00:00`).toLocaleDateString("en-AU", { weekday: "short", day: "numeric", month: "short" })
}

export default function Notifications() {
  const [ notifications, setNotifications ] = useState(null)
  const [ reminders, setReminders ] = useState(null)
  const [ cleanerNotes, setCleanerNotes ] = useState(null)

  const [ error, setError ] = useState(null)

  useEffect(() => {
    fetchNotifications()
      .then((data) => setNotifications(data.notifications))
      .catch((err) => setError(err.message))
    loadReminders()
    loadCleanerNotes()
  }, [])

  // Only marked read once actually opened/acknowledged -- just landing on the
  // inbox (e.g. an accidental tap) shouldn't silently clear anything unread.
  function handleRead(id) {
    setNotifications((all) => all.map((n) => (n.id === id ? { ...n, read_at: n.read_at || new Date().toISOString() } : n)))
    markNotificationRead(id).catch(() => {})
  }

  function handleReadAll() {
    const now = new Date().toISOString()
    setNotifications((all) => all.map((n) => ({ ...n, read_at: n.read_at || now })))
    markAllNotificationsRead().catch(() => {})
  }

  function loadReminders() {
    fetchGuestReminders().then(setReminders)
  }

  function loadCleanerNotes() {
    fetchCleanerNotes().then(setCleanerNotes)
  }

  async function handleDismiss(id) {
    await dismissGuestReminder(id)
    loadReminders()
  }

  async function handleSnooze(id) {
    await snoozeGuestReminder(id)
    loadReminders()
  }

  async function handleResolveNote(id) {
    await resolveCleanerNote(id)
    loadCleanerNotes()
  }

  const nothingYet = notifications?.length === 0 && reminders?.length === 0 && cleanerNotes?.length === 0

  return (
    <div className="mx-auto max-w-2xl px-6 py-12">
      <h1 className="text-2xl font-semibold text-foreground">Inbox</h1>

      {error && <p className="mt-6 text-destructive">Couldn't load the inbox: {error}</p>}
      {nothingYet && <p className="mt-8 text-muted-foreground">Nothing yet.</p>}

      {cleanerNotes?.length > 0 && (
        <section className="mt-6">
          <h2 className="text-sm font-medium text-muted-foreground">Notes from cleaners</h2>
          <ul className="mt-2 divide-y divide-border">
            {cleanerNotes.map((note) => (
              <li key={note.id} className="py-3">
                <div className="flex items-center justify-between gap-4">
                  <div>
                    <p className="text-foreground">
                      {note.kind === "shopping_item" && <Badge variant="outline" className="mr-2">shopping</Badge>}
                      {note.cleaner_name} — {note.property_name}
                    </p>
                    <p className="text-sm text-muted-foreground">{note.body}</p>
                    <p className="text-xs text-muted-foreground">{timeAgo(note.created_at)}</p>
                  </div>
                  <Button variant="ghost" size="sm" className="shrink-0" onClick={() => handleResolveNote(note.id)}>
                    Resolve
                  </Button>
                </div>
              </li>
            ))}
          </ul>
        </section>
      )}

      {reminders?.length > 0 && (
        <section className="mt-6">
          <h2 className="text-sm font-medium text-muted-foreground">Guest messages</h2>
          <ul className="mt-2 divide-y divide-border">
            {reminders.map((reminder) => (
              <li key={reminder.id} className="py-3">
                <div className="flex items-center justify-between gap-4">
                  <div>
                    <p className="text-foreground">
                      {REMINDER_LABELS[reminder.kind]} — {reminder.property_name} ({reminder.guest_label})
                    </p>
                    <p className={reminder.overdue ? "text-sm font-medium text-amber-700 dark:text-amber-300" : "text-sm text-muted-foreground"}>
                      {reminder.overdue ? "Due now" : "Due"} · {reminder.due_label}
                      <span className="text-muted-foreground"> · stay {formatDate(reminder.starts_on)} – {formatDate(reminder.ends_on)}</span>
                    </p>
                    {!reminder.guest_phone && (
                      <p className="text-sm text-amber-700 dark:text-amber-300">
                        ⚠ No phone number yet —{" "}
                        <Link to={routes.editBooking(reminder.booking_id)} className="underline">add it first</Link>.
                      </p>
                    )}
                  </div>
                  <Button asChild size="sm" className="shrink-0" variant={reminder.guest_phone ? "default" : "outline"}>
                    <Link to={routes.sendMessage(reminder.booking_id) + `?kind=${reminder.kind}`}>Send</Link>
                  </Button>
                </div>
                <div className="mt-2 flex gap-2">
                  <Button variant="ghost" size="sm" onClick={() => handleSnooze(reminder.id)}>Snooze</Button>
                  <Button variant="ghost" size="sm" onClick={() => handleDismiss(reminder.id)}>Dismiss</Button>
                </div>
              </li>
            ))}
          </ul>
        </section>
      )}

      {notifications?.length > 0 && (
        <section className="mt-6">
          <div className="flex items-center justify-between">
            <h2 className="text-sm font-medium text-muted-foreground">Activity</h2>
            {notifications.some((n) => !n.read_at) && (
              <Button variant="ghost" size="sm" onClick={handleReadAll}>Mark all read</Button>
            )}
          </div>
          <ul className="mt-2 divide-y divide-border">
            {notifications.map((notification) => {
              const hasLink = notification.url && notification.url !== routes.notifications
              return (
                <li key={notification.id} className={`py-3 ${notification.read_at ? "opacity-70" : ""}`}>
                  {notification.title && <p className="font-medium text-foreground">{notification.title}</p>}
                  <p className={notification.title ? "text-sm text-foreground" : "text-foreground"}>{notification.message}</p>
                  <p className="text-sm text-muted-foreground">
                    {timeAgo(notification.created_at)}
                    {hasLink && (
                      <> · <Link to={notification.url} className="underline" onClick={() => handleRead(notification.id)}>Open</Link></>
                    )}
                    {!hasLink && !notification.read_at && (
                      <> · <button type="button" className="underline" onClick={() => handleRead(notification.id)}>Mark read</button></>
                    )}
                  </p>
                </li>
              )
            })}
          </ul>
        </section>
      )}
    </div>
  )
}
